// src/pages/AdvertisementAdd.tsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthApi } from '../hooks/use-auth-api';

interface ImageItem {
  file?: File;
  url: string;
  isPrimary: boolean;
}

const AdvertisementAdd: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthApi();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'sell',
    category: '',
    description: '',
    price: '',
    province: '',
    contact_phone: '',
    cultivation_stage: 'کشت'  // ← فیلد جدید
  });
  const [images, setImages] = useState<ImageItem[]>([]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  if (!isAuthenticated || !user) {
    return (
      <div style={{ direction: 'rtl', padding: '50px', textAlign: 'center' }}>
        <h2>⚠️ لطفاً ابتدا وارد شوید</h2>
        <Link to="/login" style={{ color: '#2e7d32' }}>رفتن به صفحه ورود</Link>
      </div>
    );
  }

  const uploadImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch('https://fellfell.ir/api/upload-ad.php', {
      method: 'POST',
      credentials: 'include',
      body: formData
    });

    const data = await response.json();
    if (data.success) {
      return data.filename;
    } else {
      throw new Error(data.message || 'خطا در آپلود تصویر');
    }
  };

  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: ImageItem[] = [];
    Array.from(files).forEach((file, idx) => {
      const url = URL.createObjectURL(file);
      newImages.push({
        file,
        url,
        isPrimary: images.length === 0 && idx === 0
      });
    });

    setImages([...images, ...newImages]);
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    if (newImages.length > 0 && images[index].isPrimary) {
      newImages[0].isPrimary = true;
    }
    setImages(newImages);
  };

  const handleSetPrimary = (index: number) => {
    const newImages = images.map((img, i) => ({
      ...img,
      isPrimary: i === index
    }));
    setImages(newImages);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.description || !formData.contact_phone) {
      showToast('عنوان، توضیحات و شماره تماس الزامی است', 'error');
      return;
    }

    if (!formData.contact_phone.match(/^09[0-9]{9}$/)) {
      showToast('شماره تماس نامعتبر است (مثال: 09123456789)', 'error');
      return;
    }

    setSaving(true);

    try {
      // ── آپلود تصاویر ──────────────────────────────────
      const uploadedImages: string[] = [];
      for (const img of images) {
        if (img.file) {
          const filename = await uploadImage(img.file);
          uploadedImages.push(filename);
        }
      }

      const payload = {
        title: formData.title,
        type: formData.type,
        category: formData.category,
        description: formData.description,
        price: formData.price ? parseFloat(formData.price) : null,
        province: formData.province,
        contact_phone: formData.contact_phone,
        cultivation_stage: formData.cultivation_stage,
        images: uploadedImages
      };

      console.log('📤 ارسال آگهی:', payload);

      const response = await fetch('https://fellfell.ir/api/advertisements.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        showToast('✅ آگهی با موفقیت ثبت شد و در انتظار تایید است', 'success');
        setTimeout(() => {
          navigate('/advertisements');
        }, 2000);
      } else {
        showToast('❌ خطا در ثبت آگهی: ' + (data.message || 'خطای نامشخص'), 'error');
      }
    } catch (err: any) {
      console.error('❌ خطا:', err);
      showToast('❌ خطا در ارتباط با سرور: ' + err.message, 'error');
    }

    setSaving(false);
  };

  return (
    <div style={{ direction: 'rtl', padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '14px 28px',
          borderRadius: '12px',
          fontSize: '15px',
          zIndex: 9999,
          background: toast.type === 'success' ? '#2e7d32' : '#c62828',
          color: '#fff',
          boxShadow: '0 6px 25px rgba(0,0,0,0.25)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <h1 style={{ color: '#1e3a1e', fontSize: '24px' }}>📢 ثبت آگهی جدید</h1>
        <Link to="/advertisements" style={{ color: '#2e7d32', textDecoration: 'none' }}>
          ← بازگشت به لیست آگهی‌ها
        </Link>
      </div>

      <form onSubmit={handleSubmit} style={{ background: '#fff', padding: '30px', borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
        {/* ── اطلاعات اصلی ────────────────────────────── */}
        <h3 style={{ color: '#1e3a1e', marginBottom: '15px' }}>📋 اطلاعات آگهی</h3>

        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
            عنوان آگهی <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
            style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
            placeholder="مثلاً: فروش کود اوره"
            required
          />
        </div>

        <div style={{ display: 'flex', gap: '15px', marginBottom: '18px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
              نوع آگهی <span style={{ color: '#d32f2f' }}>*</span>
            </label>
            <select
              value={formData.type}
              onChange={e => setFormData({ ...formData, type: e.target.value })}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
              required
            >
              <option value="sell">🟢 فروش</option>
              <option value="buy">🔵 خرید</option>
              <option value="request">🟡 درخواست</option>
              <option value="cooperation">🟣 همکاری</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
              دسته‌بندی
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={e => setFormData({ ...formData, category: e.target.value })}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
              placeholder="مثلاً: کود، سم، بذر"
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '15px', marginBottom: '18px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
              مرحله کشت <span style={{ color: '#d32f2f' }}>*</span>
            </label>
            <select
              value={formData.cultivation_stage}
              onChange={e => setFormData({ ...formData, cultivation_stage: e.target.value })}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
              required
            >
              <option value="کشت">🌱 کشت (کاشت)</option>
              <option value="داشت">🌿 داشت (داشت و رشد)</option>
              <option value="برداشت">🌾 برداشت</option>
              <option value="نگهداشت">🏺 نگهداشت (نگهداری و انبارداری)</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
              استان
            </label>
            <input
              type="text"
              value={formData.province}
              onChange={e => setFormData({ ...formData, province: e.target.value })}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
              placeholder="مثلاً: تهران، اصفهان"
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '15px', marginBottom: '18px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
              قیمت (تومان)
            </label>
            <input
              type="number"
              value={formData.price}
              onChange={e => setFormData({ ...formData, price: e.target.value })}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px' }}
              placeholder="مثلاً: 250000 (در صورت توافقی خالی بگذارید)"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
              شماره تماس <span style={{ color: '#d32f2f' }}>*</span>
            </label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={e => setFormData({ ...formData, contact_phone: e.target.value })}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px', direction: 'ltr' }}
              placeholder="09123456789"
              required
            />
          </div>
        </div>

        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>
            توضیحات <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            rows={5}
            style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #ddd', fontSize: '15px', resize: 'vertical' }}
            placeholder="توضیحات کامل آگهی را وارد کنید..."
            required
          />
        </div>

        {/* ── تصاویر ────────────────────────────────────── */}
        <h3 style={{ color: '#1e3a1e', marginBottom: '15px', marginTop: '25px' }}>🖼️ تصاویر آگهی</h3>

        <div style={{ marginBottom: '18px' }}>
          <div style={{
            border: '2px dashed #ddd',
            borderRadius: '10px',
            padding: '20px',
            textAlign: 'center',
            cursor: 'pointer',
            background: '#fafafa'
          }}
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#2e7d32'; }}
          onDragLeave={(e) => { e.currentTarget.style.borderColor = '#ddd'; }}
          onDrop={(e) => {
            e.preventDefault();
            const files = e.dataTransfer.files;
            if (files.length > 0) {
              const newImages: ImageItem[] = [];
              Array.from(files).forEach((file, idx) => {
                const url = URL.createObjectURL(file);
                newImages.push({
                  file,
                  url,
                  isPrimary: images.length === 0 && idx === 0
                });
              });
              setImages([...images, ...newImages]);
            }
          }}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleAddImage}
              style={{ display: 'none' }}
              id="image-upload"
            />
            <label htmlFor="image-upload" style={{ cursor: 'pointer', display: 'block' }}>
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>📤</div>
              <p style={{ color: '#888' }}>برای آپلود تصاویر کلیک کنید یا بکشید</p>
              <p style={{ fontSize: '12px', color: '#aaa' }}>می‌توانید چندین تصویر انتخاب کنید</p>
            </label>
          </div>

          {images.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '15px' }}>
              {images.map((img, index) => (
                <div key={index} style={{ position: 'relative', width: '100px', height: '100px', borderRadius: '8px', overflow: 'hidden', border: img.isPrimary ? '3px solid #2e7d32' : '1px solid #ddd' }}>
                  <img src={img.url} alt={`تصویر ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(198,40,40,0.8)', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '14px' }}
                  >
                    ✕
                  </button>
                  {!img.isPrimary && (
                    <button
                      type="button"
                      onClick={() => handleSetPrimary(index)}
                      style={{ position: 'absolute', bottom: '4px', left: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 8px', fontSize: '10px', cursor: 'pointer' }}
                    >
                      اصلی
                    </button>
                  )}
                  {img.isPrimary && (
                    <span style={{ position: 'absolute', bottom: '4px', left: '4px', background: '#2e7d32', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>
                      ✅ اصلی
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <Link to="/advertisements" style={{
            padding: '12px 24px',
            background: '#e0e0e0',
            color: '#333',
            textDecoration: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '500'
          }}>
            انصراف
          </Link>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 30px',
              background: '#2e7d32',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? '⏳ در حال ثبت...' : '📢 ثبت آگهی'}
          </button>
        </div>
      </form>

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AdvertisementAdd;